import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CheckCircle, Circle, ArrowSquareOut, Sparkle, Copy, PaperPlaneTilt } from "@phosphor-icons/react";

const HELP_ICONS = { critical: "🔴", nearing: "🟡", done: "🟢" };

export default function DashGoLive() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/go-live/status").then((r) => setState(r.data)).catch((err) => { console.error("go-live load failed", err); toast.error("Could not load status. Try Refresh."); });
  useEffect(() => { load(); }, []);

  const notifyFirstGuest = async () => {
    setBusy(true);
    try {
      const g = await api.get("/guests");
      const first = g.data.find((x) => x.status === "waiting");
      if (!first) { toast.error("Add a guest first — then come back and hit this."); setBusy(false); return; }
      await api.post(`/guests/${first.id}/notify`, {});
      toast.success(`Texted ${first.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test notification failed");
    }
    setBusy(false);
  };

  const copyPublic = () => {
    const url = `${window.location.origin}${state?.public_url || ""}`;
    navigator.clipboard.writeText(url);
    toast.success("Public join link copied");
  };

  if (!state) return <div>Loading…</div>;
  const critical = state.checks.filter((c) => !c.done && c.critical_for === "billing");
  const criticalDone = critical.length === 0;

  return (
    <div>
      <div className="overline">Beta launch</div>
      <h1 className="text-3xl mb-2">Go-Live checklist</h1>
      <p className="text-[#5C6B61] mb-6 max-w-2xl">Everything you need to confirm before flipping BuzzerAI on for real guests. Green means done. You can share the link below with your team so multiple people can help finish the setup.</p>

      <div className="paper-card p-6 mb-6" data-testid="go-live-summary">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="overline">Progress</div>
            <div className="text-3xl font-heading font-extrabold">{state.done}/{state.total} <span className="text-base text-[#5C6B61]">({state.percent}%)</span></div>
          </div>
          <div className="flex items-center gap-2">
            {state.signature && (state.signature.envelope_status || state.signature.loa_generated) && (
              <span className={`tag text-[10px] ${(state.signature.envelope_status === "signed") ? "tag-notified" : (state.signature.envelope_status === "bounced") ? "tag-cancelled" : "tag-notified"}`} data-testid="signature-status-chip" title={`Provider: ${state.signature.envelope_provider || "download-only"}`}>
                LOA {state.signature.envelope_status ? `· ${state.signature.envelope_status}` : (state.signature.loa_emailed ? "· emailed" : "· generated")}
              </span>
            )}
            <button onClick={copyPublic} className="btn-ghost !py-2 !px-3 text-xs" data-testid="go-live-copy-public"><Copy size={14} /> Copy public link</button>
            <button onClick={load} className="btn-ghost !py-2 !px-3 text-xs" data-testid="go-live-refresh">Refresh</button>
          </div>
        </div>
        <div className="h-2 rounded-full bg-[#E6E2D6] overflow-hidden mt-4">
          <div className="h-full bg-[#1E3A2F] transition-[width]" style={{ width: `${state.percent}%` }} />
        </div>
        {!criticalDone && (
          <div className="mt-4 p-3 rounded-lg bg-[#EEE0B8]/70 text-[11px] text-[#7A5A00]">
            <b>Heads up:</b> Stripe is in test mode. You can beta-test with friendly customers now; before you charge real money, add your live <code className="bg-white px-1 rounded">STRIPE_SECRET_KEY</code> in the deployment env settings.
          </div>
        )}
      </div>

      <div className="space-y-3">
        {state.checks.map((c) => (
          <div key={c.key} className="paper-card p-5 flex items-start gap-4" data-testid={`go-live-check-${c.key}`}>
            <div className="pt-0.5">
              {c.done ? <CheckCircle size={24} color="#1E3A2F" weight="fill" /> : <Circle size={24} color="#5C6B61" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{c.label}</div>
              <div className="text-xs text-[#5C6B61] mt-1">{c.help}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {c.key === "first_sms" && !c.done && state.counts.guests > 0 && (
                <button onClick={notifyFirstGuest} disabled={busy} className="btn-primary !py-1.5 !px-3 text-xs" data-testid="go-live-send-test-sms">
                  <PaperPlaneTilt size={14} color="#fff" /> Text now
                </button>
              )}
              {c.cta && (
                <Link to={c.cta} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`go-live-cta-${c.key}`}>
                  {c.done ? "Review" : "Go"} <ArrowSquareOut size={12} />
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="paper-card p-6 mt-8" data-testid="go-live-beta-guide">
        <div className="flex items-center gap-2 mb-2"><Sparkle size={16} color="#D96C5B" weight="fill" /><div className="overline">Beta operator onboarding</div></div>
        <h3 className="text-xl mb-2">Share the Beta Onboarding Guide with your first restaurants</h3>
        <p className="text-sm text-[#5C6B61] mb-4">A one-page walkthrough your invitees can follow in 15 minutes — first guest, first SMS, first call. Send it in the invite email.</p>
        <Link to="/beta-guide" target="_blank" className="btn-primary text-sm inline-flex" data-testid="go-live-view-beta-guide">
          Open the guide <ArrowSquareOut size={14} color="#fff" />
        </Link>
      </div>
    </div>
  );
}
